export const AV1_CONFIG = Object.freeze({
  codec: 'av01.0.04M.08',
  codedWidth: 1920,
  codedHeight: 1105,
  optimizeForLatency: true,
  hardwareAcceleration: 'prefer-hardware'
});

const WASM_ALIASES = new Set([
  'av1-wasm',
  'wasm-av1',
  'av1wasm',
  'wasmav1',
  'dav1d',
  'wasm'
]);

export async function selectAv1WebCodecsConfig(scope = globalThis, { excludePreferences = [] } = {}) {
  if (typeof scope.VideoDecoder === 'undefined'
    || typeof scope.EncodedVideoChunk === 'undefined'
    || typeof scope.OffscreenCanvas === 'undefined') {
    return null;
  }
  // Hardware acceleration is a preference, not a WebCodecs requirement. Linux
  // can reject prefer-hardware while its native AV1 software decoder works.
  for (const hardwareAcceleration of ['prefer-hardware', 'prefer-software']) {
    if (excludePreferences.includes(hardwareAcceleration)) continue;
    const config = { ...AV1_CONFIG, hardwareAcceleration };
    try {
      const support = await scope.VideoDecoder.isConfigSupported(config);
      if (support?.supported) return config;
    } catch (error) {
      scope.console?.warn('[wanli-video] AV1 capability probe failed', { hardwareAcceleration, error: String(error) });
    }
  }
  return null;
}

export async function detectAv1WebCodecsSupport(scope = globalThis) {
  return Boolean(await selectAv1WebCodecsConfig(scope));
}

export function resolveAv1Backend({ override = '', webCodecsSupported = false, webCodecsDisabled = false } = {}) {
  const normalized = String(override || '').trim().toLowerCase();
  if (WASM_ALIASES.has(normalized)) return 'wasm';
  return webCodecsSupported && !webCodecsDisabled ? 'webcodecs' : 'wasm';
}

export function buildVideoStreamUrl({ location, tableIndex = 0, grant = {} } = {}) {
  const safeIndex = Math.max(0, Math.min(7, Number(tableIndex) || 0));
  const protocol = location?.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = String(location?.host || '');
  const base = `${protocol}//${host}/AV1/videovip${safeIndex + 1}`;
  if (!grant?.enabled || !grant?.grantToken) return base;
  const queryName = grant.queryParamName || 'vg';
  return `${base}?${encodeURIComponent(queryName)}=${encodeURIComponent(grant.grantToken)}`;
}

export function resolveVideoRenderSize({ cssWidth, devicePixelRatio = 1, sourceWidth = 1920, sourceHeight = 1105 } = {}) {
  const safeSourceWidth = Math.max(1, Number(sourceWidth) || 1920);
  const safeSourceHeight = Math.max(1, Number(sourceHeight) || 1105);
  const safeCssWidth = Math.max(1, Number(cssWidth) || safeSourceWidth);
  const safeDpr = Math.max(1, Math.min(2, Number(devicePixelRatio) || 1));
  const width = Math.min(safeSourceWidth, Math.max(640, Math.min(1600, Math.round(safeCssWidth * safeDpr))));
  return { width, height: Math.max(1, Math.round(width * (safeSourceHeight / safeSourceWidth))) };
}

export function computeAdaptiveVideoPlaybackSpeed(queueSize, { catchupStart = 3, catchupFull = 20, min = 1, max = 1.25 } = {}) {
  const size = Math.max(0, Number(queueSize) || 0);
  if (size <= catchupStart) return min;
  const span = Math.max(1, catchupFull - catchupStart);
  return min + ((max - min) * Math.min(1, (size - catchupStart) / span));
}

export function computeVideoFrameDrop({ queueSize, fastStart = false, targetFrames = 10 } = {}) {
  const size = Math.max(0, Number(queueSize) || 0);
  if (fastStart && size > targetFrames) return { count: size - targetFrames, overThreshold: false };
  // Steady live play must preserve the entire dealing action. Only hidden
  // startup replay may skip frames; sustained overload is an explicit error.
  return { count: 0, overThreshold: false };
}

export function advanceVideoRenderClock(previous, now, interval) {
  if (!previous || now - previous > interval * 2) return now;
  return previous + interval;
}

export function shouldRevealVideo({
  hasFrame = false,
  sawFastStartMeta = false,
  fastStartActive = true,
  fastStartEndSeen = false,
  noMetadataGraceExpired = false,
  forceExpired = false
} = {}) {
  if (!hasFrame) return false;
  if (forceExpired) return true;
  if (fastStartEndSeen) return true;
  if (sawFastStartMeta) return !fastStartActive;
  return noMetadataGraceExpired;
}
